import { useState } from 'react'
import { Button, Card, Text, TextField, Flex, Badge, Heading } from '@radix-ui/themes'
import { Download, Calendar } from 'lucide-react'
import { listBillsForExport, markBillsExported } from '../services/data'
import type { Bill } from '../types'

interface QuickBooksExportProps {
  onClose?: () => void
}

// IIF format constants (matching legacy VB.NET export)
const IIF_HEADERS = [
  '!TRNS\tTRNSTYPE\tDATE\tACCNT\tNAME\tAMOUNT\tDOCNUM',
  '!SPL\tTRNSTYPE\tDATE\tACCNT\tNAME\tAMOUNT\tDOCNUM\tINVITEM\tEXTRA',
  '!ENDTRNS'
]

export function QuickBooksExport({ onClose }: QuickBooksExportProps) {
  const [startDate, setStartDate] = useState(() => {
    const d = new Date()
    d.setDate(1)
    return d.toISOString().split('T')[0]
  })
  const [endDate, setEndDate] = useState(() => {
    const d = new Date()
    d.setMonth(d.getMonth() + 1)
    d.setDate(0)
    return d.toISOString().split('T')[0]
  })
  const [loading, setLoading] = useState(false)
  const [bills, setBills] = useState<Bill[]>([])
  const [exported, setExported] = useState(false)

  const fetchBills = async () => {
    setLoading(true)
    try {
      const data = await listBillsForExport(startDate, endDate)
      setBills(data)
      setExported(false)
    } catch (e) {
      console.error('Failed to fetch bills for export', e)
    } finally {
      setLoading(false)
    }
  }

  const generateIIF = (): string => {
    const lines = [...IIF_HEADERS]

    bills.forEach(b => {
      const taxState = (b as any).tax_rates?.state || 'WV'
      const customerName = b.customer_name || 'Unknown'
      const billDate = b.bill_date?.split('T')[0] || ''
      const billCode = b.bill_code || ''
      const wholeInvoice = Number(b.whole_invoice) || 0
      const plantTotal = Number(b.plant_total) || 0
      const materialTotal = Number(b.material_total) || 0
      const laborTotal = Number(b.labor_total) || 0
      const discountAmount = Number(b.whole_invoice_discount_amount) || 0
      const salesTaxAmount = Number(b.sales_tax_amount) || 0

      // Transaction header (Accounts Receivable)
      lines.push(`TRNS\tINVOICE\t${billDate}\tAccounts receivable - trade\t${customerName}\t${wholeInvoice.toFixed(2)}\t${billCode}`)

      // Plant line
      if (plantTotal > 0) {
        lines.push(`SPL\tINVOICE\t${billDate}\tSales-plants-${taxState}\t\t${plantTotal.toFixed(2)}\t${billCode}\tPlants\t`)
      }

      // Material line
      if (materialTotal > 0) {
        lines.push(`SPL\tINVOICE\t${billDate}\tSales-materials & hardgoods-${taxState}\t\t${materialTotal.toFixed(2)}\t${billCode}\tMaterials\t`)
      }

      // Labor line
      if (laborTotal > 0) {
        lines.push(`SPL\tINVOICE\t${billDate}\tSales-labor-${taxState}\t\t${laborTotal.toFixed(2)}\t${billCode}\tLabor\t`)
      }

      // Discount line
      if (discountAmount > 0) {
        lines.push(`SPL\tINVOICE\t${billDate}\tSales-Discount-${taxState}\t\t${(-discountAmount).toFixed(2)}\t${billCode}\tDiscount\t`)
      }

      // Sales tax line
      if (salesTaxAmount > 0) {
        lines.push(`SPL\tINVOICE\t${billDate}\tContracting Sales Tax - ${taxState}\t\t${salesTaxAmount.toFixed(2)}\t${billCode}\tSales Tax\t`)
      }

      lines.push('ENDTRNS')
    })

    return lines.join('\n')
  }

  const handleExport = async () => {
    if (bills.length === 0) {
      await fetchBills()
      return
    }

    setLoading(true)
    try {
      const iifContent = generateIIF()
      const blob = new Blob([iifContent], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const dateStr = new Date().toISOString().split('T')[0]
      a.download = `QuickBooks_Invoice_Export_${dateStr}.iif`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      // Mark bills as exported
      await markBillsExported(bills.map(b => b.id))
      setExported(true)
      setBills([])
    } catch (e) {
      console.error('Export failed', e)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card size="2" style={{ maxWidth: 600, margin: '0 auto' }}>
      <Flex direction="column" gap="4" p="4">
        <Flex align="center" gap="2">
          <Calendar size={20} />
          <Heading size="4">QuickBooks Export</Heading>
        </Flex>

        <Flex direction="column" gap="2">
          <Text size="2" weight="bold">Date Range</Text>
          <Flex gap="2">
            <TextField.Root
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              style={{ flex: 1 }}
            />
            <TextField.Root
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              style={{ flex: 1 }}
            />
          </Flex>
        </Flex>

        {bills.length > 0 && (
          <Text size="2" color="gray">
            Found <Badge color="blue">{bills.length}</Badge> bills to export
          </Text>
        )}

        {exported && (
          <Text size="2" color="green">
            Exported successfully! Bills marked as exported in QuickBooks.
          </Text>
        )}

        <Flex gap="2" justify="end">
          <Button variant="soft" onClick={onClose}>
            Close
          </Button>
          <Button
            onClick={bills.length === 0 ? fetchBills : handleExport}
            disabled={loading}
          >
            {loading ? 'Loading...' : bills.length === 0 ? 'Find Bills' : <><Download size={16} /> Export .IIF</>}
          </Button>
        </Flex>
      </Flex>
    </Card>
  )
}
